/**
 * arrowsSelector.tsx
 */
import ArrowRightIcon from '@components/atoms/icons/arrowRight';
import ArrowLeftIcon from '@components/atoms/icons/arrowLeft';
import ArrowsSelectorContainer from '@components/atoms/container/arrowsSelectorContainer';
import MainContext from '@shared/components/utils/context/mainContext';
import Pages from '@pages';
import Rail from '@shared/classes/rail';
import RailArrowContainer from '@components/atoms/container/railArrowContainer';
import useInterval from '@shared/components/utils/hooks/useInterval';
import { RailDisplayMode } from '@shared/types/enums/rail';
import { selectRoutePage } from '@shared/store/slices/router.slice';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { useAppSelector } from '@shared/store/hooks';

/**
 * Props for the ArrowsSelector component.
 */
interface Props {
  rail?: Rail;
  hasTitle?: boolean;
  hIndex: number;
  hovered?: boolean;
}

/**
 * Arrows selector component. Displays arrows upon mouse enter and leave.
 * Allows to navigate through the rail items.
 */
export default function ArrowsSelector({
  rail,
  hasTitle,
  hIndex,
  hovered,
}: Props) {
  const page = useAppSelector(selectRoutePage);
  const { changeAsset } = useContext(MainContext);
  const { interval, cancelInterval } = useInterval(200);
  const hIndexRef = useRef(hIndex);
  hIndexRef.current = hIndex;

  // Calculate the arrow width and height based on the rail display mode
  const { arrowWidth, arrowHeight } = useMemo(() => {
    switch (rail?.displayMode) {
      case RailDisplayMode.EPG:
        return {
          arrowWidth: 32,
          arrowHeight: 32,
        };
      case RailDisplayMode.FILTER:
        return {
          arrowWidth: 24,
          arrowHeight: 24,
        };
      case RailDisplayMode.SQUARE:
        return {
          arrowWidth: 25,
          arrowHeight: 35,
        };
      default:
        return {
          arrowWidth: 35,
          arrowHeight: 70,
        };
    }
  }, [rail?.displayMode]);

  // Calculate if the left and right arrows should be displayed
  const { displayLeft, displayRight } = useMemo(() => {
    return {
      displayLeft: hIndex > 0,
      displayRight: rail?.circular || hIndex < (rail?.data?.length || 0) - 1,
    };
  }, [hIndex, rail]);

  // Cancel the interval if the index is out of bounds
  useEffect(() => {
    if (rail?.circular) return;
    if (hIndex <= 0 || hIndex >= (rail?.data?.length || 0) - 1) {
      cancelInterval();
    }
  }, [hIndex, rail]);

  // Render the arrows
  return rail ? (
    <ArrowsSelectorContainer
      displayMode={rail?.displayMode}
      displayTitle={hasTitle ?? !!rail?.title}
      isSearch={page === Pages.SEARCH}
    >
      {displayLeft ? (
        <RailArrowContainer
          arrowDirection='left'
          hovered={hovered}
          railDisplayMode={rail?.displayMode}
          onMouseEnter={() =>
            interval(() => changeAsset(rail.id, hIndexRef.current - 1))
          }
          onMouseLeave={cancelInterval}
        >
          <ArrowLeftIcon
            width={arrowWidth}
            height={arrowHeight}
            color={'#FFFFFF'}
          />
        </RailArrowContainer>
      ) : (
        <div />
      )}
      {displayRight ? (
        <RailArrowContainer
          arrowDirection='right'
          hovered={hovered}
          railDisplayMode={rail?.displayMode}
          onMouseEnter={() =>
            interval(() => changeAsset(rail.id, hIndexRef.current + 1))
          }
          onMouseLeave={cancelInterval}
        >
          <ArrowRightIcon
            width={arrowWidth}
            height={arrowHeight}
            color={'#FFFFFF'}
          />
        </RailArrowContainer>
      ) : (
        <div />
      )}
    </ArrowsSelectorContainer>
  ) : null;
}
